/**
 * Column Profiling Framework v2 — FE-owned pieces only. The profiling values
 * themselves (the metric grid, the distribution chart) are fully backend-driven
 * per the profiling-preview contract (see `dataStudioColumnProfileMappers.ts`):
 * the API sends a free-form `profiling_summary` map and a complete Vega-Lite
 * `profiling_visual_spec`, so the frontend has no fixed metric catalog or
 * chart-building logic left to own for those. What the frontend still owns:
 * the display label per kind, and the Transform-menu catalog below.
 */
import type { DataStudioColumnKind } from "./dataStudioTables.types";
import type { DataStudioColumnTransformCatalog } from "./dataStudioColumnTransforms.types";

export interface DataStudioColumnTypeConfig {
  /** Label rendered under the column name (e.g. "ID", "DIMENSION"). */
  label: string;
}

export const COLUMN_TYPE_CONFIG: Record<
  DataStudioColumnKind,
  DataStudioColumnTypeConfig
> = {
  id: { label: "ID" },
  dimension: { label: "Dimension" },
  string: { label: "String" },
  integer: { label: "Integer" },
  numeric: { label: "Numeric" },
  datetime: { label: "DateTime" },
  geo: { label: "Geo" },
  boolean: { label: "Boolean" },
};

/**
 * Fixed Transform-dropdown catalog per column kind, from the Data Studio V2
 * Figma "Transform" menu crop. Wand icon in the column header opens this list
 * for the column's `kind`; picking an entry drives
 * `ApplyDataStudioColumnTransformRequest.transformId`.
 */
export const COLUMN_TRANSFORM_CATALOG: DataStudioColumnTransformCatalog = {
  id: [
    {
      id: "id.clean-format",
      label: "Clean ID format",
      description: "Strip special chars and standardize",
    },
    {
      id: "id.check-uniqueness",
      label: "Check uniqueness",
      description: "Verify primary key integrity",
    },
    {
      id: "id.pad-zeros",
      label: "Pad zeros",
      description: "Standardize ID length",
    },
  ],
  dimension: [
    {
      id: "dimension.group-rare-categories",
      label: "Group rare categories",
      description: "Bucket 'Other' (<2% freq)",
    },
    {
      id: "dimension.fuzzy-deduplicate",
      label: "Fuzzy deduplicate",
      description: "Merge similar text entries",
    },
    {
      id: "dimension.pivot-to-boolean",
      label: "Pivot to boolean",
      description: "Create flags for categories",
    },
    {
      id: "dimension.translate-values",
      label: "Translate values",
      description: "Localize category names",
    },
  ],
  string: [
    {
      id: "string.trim-whitespace",
      label: "Trim whitespace",
      description: "Remove leading / trailing spaces",
    },
    {
      id: "string.extract-keywords",
      label: "Extract keywords",
      description: "Use AI to tag common topics",
    },
    {
      id: "string.sentiment-analysis",
      label: "Sentiment analysis",
      description: "Score text as Pos/Neg/Neu",
    },
    {
      id: "string.summarize-text",
      label: "Summarize text",
      description: "Generate short description",
    },
    {
      id: "string.detect-pii",
      label: "Detect PII",
      description: "Mask sensitive information",
    },
  ],
  integer: [
    {
      id: "integer.round-2-decimals",
      label: "Round to 2 decimals",
      description: "Numeric rounding for reporting",
    },
    {
      id: "integer.cap-outliers",
      label: "Cap outliers (1.5× IQR)",
      description: "Winsorise extreme values",
    },
    {
      id: "integer.impute-median",
      label: "Impute with median",
      description: "Fill nulls with statistical median",
    },
    {
      id: "integer.derive-percentile",
      label: "Derive percentile",
      description: "Calculate rank percentage",
    },
    {
      id: "integer.currency-conversion",
      label: "Currency conversion",
      description: "Apply daily exchange rate",
    },
    {
      id: "integer.binning-histogram",
      label: "Binning (Histogram)",
      description: "Standardize bucket width",
    },
  ],
  numeric: [
    {
      id: "numeric.round-2-decimals",
      label: "Round to 2 decimals",
      description: "Numeric rounding for reporting",
    },
    {
      id: "numeric.cap-outliers",
      label: "Cap outliers (1.5× IQR)",
      description: "Winsorise extreme values",
    },
    {
      id: "numeric.impute-median",
      label: "Impute with median",
      description: "Fill nulls with statistical median",
    },
    {
      id: "numeric.derive-percentile",
      label: "Derive percentile",
      description: "Calculate rank percentage",
    },
    {
      id: "numeric.currency-conversion",
      label: "Currency conversion",
      description: "Apply daily exchange rate",
    },
    {
      id: "numeric.binning-histogram",
      label: "Binning (Histogram)",
      description: "Standardize bucket width",
    },
  ],
  datetime: [
    {
      id: "datetime.parse-to-date",
      label: "Parse to date",
      description: "Cast string → date",
    },
    {
      id: "datetime.extract-month-year",
      label: "Extract month / year",
      description: "Create temporal dimensions",
    },
    {
      id: "datetime.extract-weekday",
      label: "Extract weekday",
      description: "Identify day of week",
    },
    {
      id: "datetime.relative-age",
      label: "Relative age",
      description: "Days since / until today",
    },
    {
      id: "datetime.normalize-timezone",
      label: "Normalize time zone",
      description: "Convert to UTC + 0",
    },
  ],
  geo: [
    {
      id: "geo.standardize-iso-code",
      label: "Standardize ISO code",
      description: "Convert to 2-char country codes",
    },
    {
      id: "geo.extract-lat-long",
      label: "Extract Lat / Long",
      description: "Geocode from address string",
    },
    {
      id: "geo.region-lookup",
      label: "Region lookup",
      description: "Map city to state / province",
    },
    {
      id: "geo.distance-calculation",
      label: "Distance calculation",
      description: "KM from center point",
    },
  ],
  boolean: [
    {
      id: "boolean.cast-yes-no",
      label: "Cast to Yes / No",
      description: "Convert 0/1 or T/F to labels",
    },
    {
      id: "boolean.invert-flags",
      label: "Invert flags",
      description: "Flip true / false logic",
    },
    {
      id: "boolean.fill-false",
      label: "Fill with false",
      description: "Replace nulls with default",
    },
  ],
};

export interface DataStudioResolvedColumnMetric {
  key: string;
  label: string;
  value: string;
}

/**
 * Turns a backend `profiling_summary` key (e.g. `null_count`) into display
 * copy (`Null`). Drops a trailing "count" word since every count-style metric
 * sits next to its own numeric value already — repeating "count" in the label
 * is redundant. Purely mechanical text formatting — it does not know about,
 * or hardcode, any specific metric name.
 */
const humanizeProfilingSummaryKey = (key: string): string => {
  const spaced = key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim()
    .toLowerCase()
    .replace(/\s+count$/, "");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * Expands a column's `profiling_summary` (a free-form backend map) into
 * label/value pairs in the order the backend sent them. No fixed catalog:
 * the backend owns which checks appear for a given column.
 */
export const resolveColumnMetrics = (
  profilingSummary: Record<string, string>,
): DataStudioResolvedColumnMetric[] =>
  Object.entries(profilingSummary).map(([key, value]) => ({
    key,
    label: humanizeProfilingSummaryKey(key),
    value,
  }));

/**
 * Groups resolved metrics into rows of two, matching the Figma stat-cell
 * layout (Distinct/Null, Missing/Mismatch, ...). A trailing odd metric comes
 * back as a single-item row so the caller can render it at its own natural
 * width instead of stretching it to match a paired row.
 */
export const chunkColumnMetricsIntoRows = (
  metrics: DataStudioResolvedColumnMetric[],
): DataStudioResolvedColumnMetric[][] => {
  const rows: DataStudioResolvedColumnMetric[][] = [];
  for (let index = 0; index < metrics.length; index += 2) {
    rows.push(metrics.slice(index, index + 2));
  }
  return rows;
};
